/**
 * Sitewide password gate API (lower environments only).
 *
 * Deliberately uses plain `fetch` instead of BaseApiService so it never triggers the
 * 401 -> token-refresh -> /auth/login redirect logic. These endpoints sit in front of the
 * normal auth flow.
 */
import { config } from '../config/environment';

export interface SiteGateStatus {
    required: boolean;
    unlocked: boolean;
}

/** Build a `/v1/site-gate/...` URL from the configured API base (which may or may not end in `/api`). */
function gateUrl(path: string): string {
    const base = config.apiBaseUrl;
    const prefix = base.endsWith('/api') ? `${base}/v1` : `${base}/api/v1`;
    return `${prefix}/site-gate/${path}`;
}

/** Ask the API whether the gate applies and whether this browser is already unlocked. */
export async function getSiteGateStatus(): Promise<SiteGateStatus> {
    const response = await fetch(gateUrl('status'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
        // If the status endpoint itself is unreachable, fail open (treat as not required) so a
        // network blip can't permanently lock a developer out.
        return { required: false, unlocked: true };
    }
    return response.json();
}

/**
 * Submit the shared password. Returns true when the gate cookie was set (or the gate is not
 * active for this environment).
 */
export async function unlockSiteGate(password: string): Promise<boolean> {
    const response = await fetch(gateUrl('unlock'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
    });
    return response.ok;
}
